import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { Box, Popper, Fade, Paper, ClickAwayListener, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { WV_BLUE } from '../../../theme';
import { useLocale } from '../../../locale/LocaleProvider';
import { SearchResultsList } from './SearchResultsList';
/**
 * There is no `/search` results route, so there is no Enter-to-submit — a result must be clicked
 * from the dropdown. See `SearchResultsList` for the shared results body (also used by the mobile
 * drawer search in `NavBar`).
 */
export default function NavSearch({ searchQuery, setSearchQuery, searchBuckets, searchSuggestOpen, setSearchSuggestOpen, searchPlaceholder, compact = false, onNavigate, }) {
    const anchorRef = React.useRef(null);
    const { shell } = useLocale();
    /**
     * Pin the dropdown's width to the search box's actual width. Without this, a long agency name
     * (e.g. "Bureau for Public Health-OEHS-EED") stretches the Paper up to its `maxWidth`, and since
     * the Popper anchors by one edge, the extra width spills out past the other edge instead of
     * wrapping — the dropdown visibly hangs off to one side of the input.
     */
    const [popperWidth, setPopperWidth] = React.useState();
    React.useEffect(() => {
        const el = anchorRef.current;
        if (!el)
            return undefined;
        const update = () => setPopperWidth(el.offsetWidth);
        update();
        const observer = new ResizeObserver(update);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);
    return (_jsxs(Box, { sx: { mr: 0.5 }, children: [_jsxs(Box, { ref: anchorRef, sx: {
                    display: 'flex',
                    alignItems: 'center',
                    background: '#ffffff',
                    borderRadius: 999,
                    boxShadow: '0 6px 20px rgba(2,20,45,0.08)',
                    overflow: 'hidden',
                    color: '#0b3b66',
                    px: 0.2,
                    py: 0,
                    flex: compact ? '0 1 42%' : '0 1 min(100%, 860px)',
                    minWidth: compact ? 300 : 380,
                    maxWidth: compact ? 480 : 860,
                    height: 32,
                    border: '1px solid rgba(11,54,96,0.06)',
                    flexShrink: 1,
                }, children: [_jsx(Box, { sx: { display: 'flex', alignItems: 'center', ml: 0.15, mr: 0.4 }, children: _jsx(Box, { sx: { bgcolor: WV_BLUE, width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(11,54,96,0.08)' }, children: _jsx(SearchIcon, { sx: { color: '#ffffff', fontSize: 13 } }) }) }), _jsx(TextField, { placeholder: searchPlaceholder || 'Ask about permits, licenses, agencies, or services.', size: "small", value: searchQuery, onChange: (e) => setSearchQuery(e.target.value), variant: "standard", fullWidth: true, onFocus: () => setSearchSuggestOpen(true), 
                        /** No results route to submit to — pressing Enter must not navigate. */
                        onKeyDown: (e) => {
                            if (e.key === 'Enter')
                                e.preventDefault();
                        }, InputProps: {
                            disableUnderline: true,
                            sx: {
                                color: '#0b3b66',
                                fontWeight: 400,
                                '& .MuiInputBase-input': {
                                    padding: '0 4px',
                                    fontSize: '0.8rem',
                                    lineHeight: 1.2,
                                    fontWeight: 500,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    resize: 'none',
                                    // hide native scrollbars
                                    '&::-webkit-scrollbar': { display: 'none' },
                                    '-ms-overflow-style': 'none',
                                    'scrollbar-width': 'none',
                                },
                                '& .MuiInputBase-input::placeholder': {
                                    color: 'rgba(107,114,128,0.92)',
                                    fontWeight: 600,
                                    opacity: 1,
                                    whiteSpace: 'nowrap',
                                },
                            },
                        } })] }), _jsx(Popper, { open: searchSuggestOpen && searchQuery.trim().length > 0, anchorEl: anchorRef.current, placement: "bottom-start", disablePortal: true, transition: true, modifiers: [{ name: 'offset', options: { offset: [0, 8] } }], sx: { zIndex: (z) => z.zIndex.modal, width: popperWidth }, children: ({ TransitionProps }) => (_jsx(Fade, { ...TransitionProps, timeout: 140, children: _jsx(Paper, { elevation: 10, sx: { borderRadius: 2, border: '1px solid rgba(0,40,85,0.12)', width: '100%', minWidth: 280 }, children: _jsx(ClickAwayListener, { onClickAway: () => setSearchSuggestOpen(false), children: _jsx(Box, { sx: { maxHeight: 420, overflow: 'auto' }, children: _jsx(SearchResultsList, { buckets: searchBuckets, onSelect: (hit) => {
                                        onNavigate(hit.href);
                                        setSearchSuggestOpen(false);
                                    }, sectionLabels: {
                                        departments: shell.heroSearchSectionDepartments,
                                        agencies: shell.heroSearchSectionAgencies,
                                        permits: shell.heroSearchSectionPermits,
                                    }, selectHint: shell.heroSearchSelectHint, noResultsMessage: shell.heroSearchNoResults, applyNowLabel: shell.heroSearchApplyNow }) }) }) }) })) })] }));
}
